import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";
import { useLocation, useNavigate } from "react-router";
import { api } from "./api";

export type Me = {
  id: string;
  displayName: string;
  city: string | null;
  phone: string | null;
  email: string | null;
  staffRole: "moderator" | "support" | "finance" | "admin" | null;
  mfaVerified: boolean;
  totpEnabled: boolean;
  sellerStatus: string;
  unreadNotifications: number;
  unreadDeals: number;
  locale: "en" | "ar";
  memberSince: string;
};

type Session = { me: Me | null; loading: boolean; refresh: () => Promise<Me | null> };
const Ctx = createContext<Session | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);
  const refresh = useCallback(async () => {
    try {
      const r = await api<{ user: Me | null }>("/api/me");
      setMe(r.user);
      return r.user;
    } catch {
      setMe(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    refresh();
    // Light polling keeps the unread badge fresh without a socket server.
    const id = setInterval(() => document.visibilityState === "visible" && refresh(), 30_000);
    return () => clearInterval(id);
  }, [refresh]);
  return <Ctx.Provider value={{ me, loading, refresh }}>{children}</Ctx.Provider>;
}

export function useSession() {
  const v = useContext(Ctx);
  if (!v) throw new Error("useSession outside provider");
  return v;
}

/** Returns a function that runs `action` if signed in, otherwise sends the user to sign in and back. */
export function useRequireAuth() {
  const { me } = useSession();
  const nav = useNavigate();
  const loc = useLocation();
  return useCallback(
    (action?: () => void) => {
      if (me) {
        action?.();
        return true;
      }
      nav(`/signin?next=${encodeURIComponent(loc.pathname + loc.search)}`);
      return false;
    },
    [me, nav, loc],
  );
}
